import { ApiError, chatStreamEvent, type ChatStreamEvent, type SendChatMessageRequest } from '@app/shared';

/**
 * Streaming client for the quick-chat turn (§21, WP-C4). The turn is streamed FROM
 * the POST itself (`text/event-stream`), so `EventSource` (GET-only, header-less)
 * can't carry it — we use `fetch()` + a `ReadableStream` reader and parse SSE frames
 * ourselves. This lives next to `api.ts` because the standard JSON envelope helper
 * on `ApiClient` can't return a stream.
 *
 * Contract (pinned, WP-C2/C3): `POST …/chat/messages`, Bearer auth, body
 * `sendChatMessageRequest`; the response body is a sequence of `data: <json>\n\n`
 * frames, each one `chatStreamEvent`. The stream ends after `turn_done`. An inline
 * approval decision is POSTed on a SEPARATE request (`decideChatApproval`) and
 * unblocks THIS still-open stream — we never reopen it.
 */

/**
 * Split an accumulating SSE buffer into complete frames' `data` payloads plus the
 * unterminated remainder. Pure + synchronous so it is unit-testable in isolation.
 *
 * SSE frames are separated by a blank line (`\n\n`). Within a frame we keep only
 * `data:` lines (joined with `\n` per the spec), ignoring comments (`:`-prefixed),
 * `event:`/`id:`/`retry:` fields, and blank frames (heartbeats). `\r\n` is tolerated.
 */
export function parseSseChunk(buffer: string): { data: string[]; rest: string } {
  const normalized = buffer.replace(/\r\n/g, '\n');
  const parts = normalized.split('\n\n');
  // The final element is the (possibly empty) still-incomplete frame — keep it.
  const rest = parts.pop() ?? '';
  const data: string[] = [];
  for (const frame of parts) {
    const dataLines: string[] = [];
    for (const line of frame.split('\n')) {
      if (line.startsWith('data:')) {
        // A single optional leading space after the colon is stripped (SSE spec).
        dataLines.push(line.slice(5).replace(/^ /, ''));
      }
    }
    if (dataLines.length > 0) data.push(dataLines.join('\n'));
  }
  return { data, rest };
}

export interface StreamChatMessageOptions {
  workspaceId: string;
  body: SendChatMessageRequest;
  token: string | null;
  onEvent: (event: ChatStreamEvent) => void;
  signal?: AbortSignal;
  /** Injectable for tests; defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
  /** Same-origin ('') in the web app; injectable for tests. */
  baseUrl?: string;
}

/**
 * Open the streaming turn and invoke `onEvent` for each parsed `chatStreamEvent`
 * until the stream closes (after `turn_done`). Throws `ApiError` on a non-OK
 * response (parsing the standard `{ error }` envelope) and rethrows an aborted
 * fetch's error to the caller.
 */
export async function streamChatMessage(opts: StreamChatMessageOptions): Promise<void> {
  const doFetch = opts.fetchImpl ?? fetch.bind(globalThis);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
  };
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;

  const res = await doFetch(
    `${opts.baseUrl ?? ''}/api/workspaces/${opts.workspaceId}/chat/messages`,
    { method: 'POST', headers, body: JSON.stringify(opts.body), signal: opts.signal },
  );

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    let code = 'UNKNOWN';
    let message = `Request failed (${res.status})`;
    try {
      const err = (JSON.parse(text) as { error?: { code?: string; message?: string } }).error;
      if (err?.code) code = err.code;
      if (err?.message) message = err.message;
    } catch {
      /* non-JSON error body — keep the generic message */
    }
    throw new ApiError(res.status, code, message);
  }

  if (!res.body) throw new ApiError(res.status, 'NO_BODY', 'Response had no readable body');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const emit = (raw: string): void => {
    let json: unknown;
    try {
      json = JSON.parse(raw);
    } catch {
      return; // ignore a malformed frame rather than aborting the turn
    }
    const parsed = chatStreamEvent.safeParse(json);
    if (parsed.success) opts.onEvent(parsed.data);
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { data, rest } = parseSseChunk(buffer);
    buffer = rest;
    for (const raw of data) emit(raw);
  }
  // Flush any final complete frame left without a trailing blank line.
  buffer += decoder.decode();
  const { data } = parseSseChunk(`${buffer}\n\n`);
  for (const raw of data) emit(raw);
}
